import type { GetServerSideProps, NextPage } from 'next';
import { z } from 'zod';
import Script from 'next/script';
import { getApolloSsrClient } from '@stitch-fix/sf-next/server';
import { getCachedEntry } from '../utils/requests/contentstack/cachedRequests';
import { formatError, serverLogger } from '../utils/loggers/server';
import { modularPageWrapperSchema } from '../components/ModularPageWrapper/schema';
import { ModularPageWrapper } from '../components/ModularPageWrapper';
import { isAllSettledResponseFailure } from '../utils/promises';
import { getDynamicAdsRequest } from '../features/dynamicAds/utils/getDynamicAdsRequest';
import { getModulesWithHeroOverride } from '../features/dynamicAds/utils/getModulesWithHeroOverride';
import { getExperimentHeroProps } from './getExperimentHeroProps';

export type GatewayPageProps = z.infer<typeof modularPageWrapperSchema>;

export const getServerSideProps: GetServerSideProps<GatewayPageProps> = async ({
  req,
}) => {
  try {
    const apolloSsrClient = getApolloSsrClient({ req });
    const dynamicAdsRequest = getDynamicAdsRequest({ req, apolloSsrClient });
    const entryRequest = getCachedEntry({
      contentType: 'modular_page',
      uid: 'blt3d5618ca19653a4b',
      schema: modularPageWrapperSchema,
      logger: serverLogger,
    });

    const responses = await Promise.allSettled([
      entryRequest,
      dynamicAdsRequest,
    ]);

    const [entryResponse, dynamicAdsResponse] = responses;
    const dynamicAdsData =
      dynamicAdsResponse.status === 'fulfilled'
        ? dynamicAdsResponse.value?.data
        : undefined;

    if (isAllSettledResponseFailure(entryResponse)) {
      throw entryResponse.reason;
    }

    let modules = entryResponse.value.data.modules;

    // Allocate the visitor to the hero experiment.
    // If they are allocated to the treatment the function will return the props we
    // should use for the hero, otherwise it will return null
    const experimentHeroProps = await getExperimentHeroProps({
      req,
      apolloSsrClient,
      businessLinePage: 'gateway',
    });

    // Override the hero module if we have experiment hero props
    if (experimentHeroProps) {
      modules = modules.map(module => {
        if (module.type === 'hero' || module.type === 'galleryHero') {
          return {
            type: 'hero',
            props: experimentHeroProps,
          };
        }

        return module;
      });
    }

    return {
      props: {
        seoData: entryResponse.value.data.seoData,
        modules: getModulesWithHeroOverride({
          businessLinePage: 'gateway',
          modules,
          dynamicAds: dynamicAdsData,
        }),
      },
    };
  } catch (error: unknown) {
    serverLogger({
      level: 'error',
      team: 'client-onboarding',
      message: 'Required page data is undefined - Gateway',
      context: {
        error: formatError(error),
        page: '/',
        severity: 'critical',
      },
    });

    throw new Error('Required data not provided to render Gateway page');
  }
};

const GatewayPage: NextPage<GatewayPageProps> = ({ seoData, modules }) => {
  return (
    <>
      <Script
        // Blueshift web conversion integration (https://experiences.getblueshift.com/)
        data-testid="blueshift-script"
        src="https://www.lightboxcdn.com/vendor/3767d51a-4af9-4270-8ee4-c7310e4371b0/lightbox_speed.js"
        defer
      />
      <ModularPageWrapper seoData={seoData} modules={modules} />
    </>
  );
};

export default GatewayPage;
